import { AddOnRateType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';

import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';

/**
 * Canonical rate presentation helpers. Every surface that renders a rate — freight rate
 * cards, job row rates, and both Rates History timelines — formats through here so a
 * given rate reads identically everywhere.
 *
 * Before this module the same three operations were reimplemented five times
 * (FreightRateItem, ProjectTimelineEventDetail, pricingEventsHelpers, rateUtils,
 * LoadCustomRateIndicator), and they had drifted: only some used the company currency
 * formatter, and only the pricing-events copy treated add-on percent rates as
 * percentages. See `isPercentRateType` for that divergence.
 */

/** A rate type that may arrive as a freight `RateType`, an `AddOnRateType`, or a raw wire string. */
type RateTypeLike = RateType | AddOnRateType | string | null | undefined;

/** Formats a bare number as company currency; supplied by the caller's `useCompanyCurrency()`. */
type CurrencyFormatter = (value: number | string) => string;

/**
 * Rate types whose value is a percentage rather than a currency amount.
 *
 * This deliberately spans two enums. Freight rates use `RateType`, where only
 * `RateCommission` is a percentage. Add-on rates use `AddOnRateType`, where
 * `RatePercentOfTotal` is. A pricing event's `changes[].to` can carry either, and it
 * arrives as an unvalidated string, so membership is tested by value.
 */
const PERCENT_RATE_TYPE_VALUES: ReadonlySet<string> = new Set([
  RateType.RATE_COMMISSION,
  AddOnRateType.RATE_PERCENT_OF_TOTAL,
]);

/** True when the rate's value should render as `12%` rather than `$12.00`. */
export function isPercentRateType(rateType: RateTypeLike) {
  return typeof rateType === 'string' && PERCENT_RATE_TYPE_VALUES.has(rateType);
}

/**
 * Coerce a wire value to a finite number. Rate values reach the UI as numbers from
 * models, as numeric strings from pricing-event diffs, and as `''` from a cleared
 * React Hook Form input — all three mean "no usable amount" unless finite.
 */
export function toRateNumber(value: unknown) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numeric) ? numeric : null;
}

interface RateFormatOptions {
  /** Rendered when there is no usable amount. Callers pass their own placeholder. */
  emptyValue?: string;
}

/**
 * How the rate type reads alongside the amount.
 *  - `full`  — "Per Hour", for forms and anywhere with room to spare.
 *  - `short` — "/hr", for dense surfaces like list popovers and table cells.
 */
type RateUnitStyle = 'full' | 'short';

/**
 * Abbreviated rate unit. Falls back to the full label when a rate type has no short form,
 * so a newly added `RateType` degrades to something readable instead of a raw i18n key.
 *
 * Percentage rate types return no unit at all: `formatRateAmount` already renders the `%`,
 * and "12% Commission" reads worse than "12%" in a dense list.
 */
function shortRateUnitLabel(rateType: RateTypeLike) {
  if (!rateType) {
    return '';
  }

  if (isPercentRateType(rateType)) {
    return '';
  }

  return t(`rate_types_short.${snakeCase(rateType)}`, {
    defaultValue: getRateTypeLabel(rateType as RateType),
  });
}

/**
 * Format a rate's amount alone: `$50.00`, or `12%` for percentage rate types.
 * Returns `emptyValue` when the amount is missing or non-numeric.
 */
export function formatRateAmount(
  value: unknown,
  rateType: RateTypeLike,
  currencyFormatter: CurrencyFormatter,
  { emptyValue = '' }: RateFormatOptions = {},
) {
  const amount = toRateNumber(value);

  if (amount === null) {
    return emptyValue;
  }

  return isPercentRateType(rateType) ? `${amount}%` : currencyFormatter(amount);
}

/**
 * Format a rate as amount plus friendly type: `$50.00 Per Ton`.
 *
 * When only the type is known the type label stands alone, which is how an
 * in-progress rate card renders before an amount is entered.
 */
export function formatRateWithType(
  value: unknown,
  rateType: RateTypeLike,
  currencyFormatter: CurrencyFormatter,
  {
    emptyValue = '',
    unitStyle = 'full',
  }: RateFormatOptions & { unitStyle?: RateUnitStyle } = {},
) {
  const hasAmount = toRateNumber(value) !== null;
  const isShort = unitStyle === 'short';
  const typeLabel = isShort
    ? shortRateUnitLabel(rateType)
    : rateType
      ? getRateTypeLabel(rateType as RateType)
      : '';

  if (!hasAmount) {
    return typeLabel || emptyValue;
  }

  const amount = formatRateAmount(value, rateType, currencyFormatter, { emptyValue });

  if (!typeLabel) {
    return amount;
  }

  // Slash-prefixed short units read as a direct suffix ("$125.00/hr"); word units such as
  // "flat" still need the space, and full labels always do.
  const needsSpace = !isShort || !typeLabel.startsWith('/');

  return needsSpace ? `${amount} ${typeLabel}` : `${amount}${typeLabel}`;
}
